import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Trash2 } from 'lucide-react';
import type { CheckRow } from '../../../../../Helpers/componentChecks';
import { TEMPLATE_CATEGORIES } from '../../../../BuildingChecks/types';
import { formatInterval } from '../../../../BuildingChecks/buildingChecksUtils';

interface CheckRowsProps {
    rows: CheckRow[];
    buildingKey: string;
    /** The component is excluded from the plan: nothing can be changed */
    disabled: boolean;
    /** Massredigering: how many components are selected (each row says how many of them it covers) */
    selectedCount?: number;
    /** Remove the row's checks from the building; absent for non-admins */
    onRemove?: (row: CheckRow) => void;
    /** Move the next due date of the row's checks */
    onEditDate: (row: CheckRow) => void;
}

/**
 * The building checks about the component, last in the card, one row per template.
 * They show that the work is already handled by a kontroll; they carry no cost
 * and are not part of the maintenance plan. Skickbedömning is not listed (see
 * checksCoveringComponent).
 */
export const CheckRows: React.FC<CheckRowsProps> = ({ rows, buildingKey, disabled, selectedCount, onRemove, onEditDate }) => {
    if (rows.length === 0) return null;

    return (
        <div className="component-check-section">
            <div className="component-check-heading">
                <ClipboardCheck size={16} aria-hidden="true" />
                Kontroller
                <span className="component-check-heading-note">Ingår inte i underhållskostnaden</span>
            </div>
            {rows.map(row => {
                const categoryLabel = TEMPLATE_CATEGORIES.find(c => c.value === row.template.category)?.label;
                return (
                    <div key={row.templateId} className={`activity-row component-check-row ${disabled ? 'disabled' : ''}`}>
                        <div className="activity-name">
                            <Link to={`/building/${buildingKey}/checks`} className="component-check-name">
                                {row.name}
                            </Link>
                            {categoryLabel && <span className="component-check-chip">{categoryLabel}</span>}
                            {selectedCount !== undefined && (
                                <span className="bulk-row-presence" title="Hur många av de valda komponenterna som har en kontroll av mallen">
                                    på {row.checks.length} av {selectedCount}
                                </span>
                            )}
                        </div>
                        <div className="activity-interval">{row.interval ? formatInterval(row.interval) : 'Olika'}</div>
                        <div className="activity-area"><span className="text-muted">–</span></div>
                        <div className="activity-year">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 component-check-date"
                                onClick={() => onEditDate(row)}
                                disabled={disabled}
                                title="Ändra nästa datum"
                                aria-label={`Ändra nästa datum för ${row.name}`}
                            >
                                {row.nextDueDate ?? 'Olika datum'}
                            </button>
                        </div>
                        <div className="activity-cost" />
                        <div className="activity-overdue-indicator">
                            {onRemove && (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 activity-remove-button"
                                    onClick={() => onRemove(row)}
                                    disabled={disabled}
                                    title={row.checks.length === 1 ? 'Ta bort kontroll' : 'Ta bort kontrollerna'}
                                    aria-label={`Ta bort ${row.name}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
